import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import appConfig from '../config';

/**
 * APIM Consumer Onboarding — SAMPLE UI.
 *
 * This is a deliberately plain, functional UI to exercise the backend
 * end-to-end (submit → queue → approve/reject). The real design lands later.
 *
 * Three tabs:
 *   • New Request — the Section 1–8 form (minimal fields, live auth preview).
 *   • My Requests — the caller's own submissions + status.
 *   • Approval Queue — EASP approvers only; approve / reject.
 */

const card = 'bg-white rounded-xl border border-gray-200 p-5 shadow-sm';
const label = 'block text-sm font-medium text-gray-700 mb-1';
const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sce-green/40';
const inputError = 'w-full border border-red-400 bg-red-50/40 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400/40';
const inputValid = 'w-full border border-green-400 bg-green-50/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400/40';
const sectionTitle = 'text-base font-semibold text-sce-dark mb-3 pb-2 border-b border-gray-100';

function Field({ labelText, required, error, valid, hint, children }) {
  return (
    <div>
      <label className={label}>
        {labelText} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {children}
        {valid && !error && (
          <svg className="w-4 h-4 text-green-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      {error ? (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

function MultiSelect({ options, value, onChange }) {
  const toggle = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          aria-pressed={value.includes(opt)}
          onClick={() => toggle(opt)}
          className={
            'px-3 py-1.5 rounded-full text-xs font-medium border transition ' +
            (value.includes(opt)
              ? 'bg-sce-green text-white border-sce-green'
              : 'bg-white text-gray-600 border-gray-300 hover:border-sce-green')
          }
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Draft: { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
    Submitted: { badge: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
    InReview: { badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
    Approved: { badge: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
    Rejected: { badge: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
  };
  const s = styles[status] || styles.Draft;
  const labels = { InReview: 'In Review' };
  return (
    <span className={'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ' + s.badge}>
      <span className={'w-1.5 h-1.5 rounded-full ' + s.dot} />
      {labels[status] || status}
    </span>
  );
}

// ─── New Request form ───────────────────────────────────────────────────
// Section metadata drives the progress tracker + sticky nav. Each entry lists
// the required field paths that belong to it so we can show per-section
// completion.
const SECTIONS = [
  { id: 'sec-1', label: 'CMDB & Application', fields: ['cmdbId', 'application.name', 'application.requestType', 'application.appType', 'application.description'] },
  { id: 'sec-2', label: 'Business Purpose', fields: ['businessJustification'] },
  { id: 'sec-3', label: 'Ownership & Support', fields: ['ownership.businessOwner.email', 'ownership.businessOwner.name', 'support.groupName', 'support.groupEmail'] },
  { id: 'sec-5', label: 'Capability & Usage', fields: ['capability.types', 'capability.usagePatterns', 'capability.usageDescription'] },
  { id: 'sec-6', label: 'Authentication', fields: ['authQuestionnaire.consumerType', 'authQuestionnaire.interactionPattern'] },
  { id: 'sec-7', label: 'Financial & Cost', fields: ['financial.monthlyBudgetUsd', 'financial.overrunAction', 'costAllocation.workOrderNumber', 'costAllocation.workOrderType'] },
];

// Deep-merge a stored request over the blank template so editing an existing
// draft keeps defaults for any fields absent from an older schema. Only plain
// objects are merged recursively; arrays and scalars from `src` win outright.
function hydrateForm(blank, src) {
  if (src == null) return blank;
  const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
  const out = Array.isArray(blank) ? [...blank] : { ...blank };
  for (const key of Object.keys(src)) {
    const b = blank[key];
    const s = src[key];
    out[key] = isObj(b) && isObj(s) ? hydrateForm(b, s) : s;
  }
  return out;
}

function NewRequestForm({ enums, editing, onSubmitted, onCancelEdit }) {
  // When `editing` is a Draft record, hydrate the form from it; otherwise start
  // blank. Deep-merge over the blank template so any fields missing from an
  // older draft schema still have sane defaults.
  const blank = {
    cmdbId: '',
    application: { requestType: '', name: '', description: '', appType: '' },
    businessJustification: '',
    ownership: { businessOwner: { name: '', email: '' } },
    support: { groupName: '', groupEmail: '', adGroup: '' },
    capability: { types: [], usagePatterns: [], usageDescription: '' },
    authQuestionnaire: {
      consumerType: '',
      customDeveloped: true,
      interactionPattern: '',
      entraCapable: '',
      jwtOutbound: '',
      apiKeySupport: '',
      altIdp: { uses: false, name: '' },
      technicalConstraints: { hasConstraints: false, details: '' },
      exceptionJustification: '',
    },
    financial: { monthlyBudgetUsd: '', overrunAction: '' },
    costAllocation: { workOrderNumber: '', workOrderType: '', sapCostCenter: '' },
  };
  const [form, setForm] = useState(() => (editing ? hydrateForm(blank, editing) : blank));
  const [prefill, setPrefill] = useState(null);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [ownerHint, setOwnerHint] = useState(null);
  const [ownerResolved, setOwnerResolved] = useState(false);
  const [ownerLookupBusy, setOwnerLookupBusy] = useState(false);

  // Read a value from the form by dot-path.
  const get = (path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), form);

  // Nested setter helper.
  const set = (path, val) => {
    setDirty(true);
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = val;
      return next;
    });
    // Clear this field's error as soon as the user edits it.
    setFieldErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const markTouched = (path) => setTouched((prev) => (prev[path] ? prev : { ...prev, [path]: true }));

  useEffect(() => {
    api.getOnboardingPrefill().then(setPrefill).catch(() => {});
  }, []);

  // Warn before leaving/refreshing the tab with unsaved edits.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Returns a map of { 'dot.path': 'message' } for every invalid required
  // field. Drafts are lenient — only the CMDB CI Name is mandatory.
  const computeErrors = (asDraft) => {
    const e = {};
    const req = (path, msg) => {
      const val = get(path);
      const empty = Array.isArray(val) ? val.length === 0 : !String(val ?? '').trim();
      if (empty) e[path] = msg || 'This field is required.';
    };
    // Free-text fields that an approver reads must carry a meaningful minimum
    // length — "required" alone would let a single character like "." pass.
    const reqMin = (path, min, label) => {
      const val = String(get(path) ?? '').trim();
      if (!val) {
        e[path] = `${label} is required.`;
      } else if (val.length < min) {
        e[path] = `${label} must be at least ${min} characters.`;
      }
    };
    // All email fields must be a valid @sce.com address. `label` is used in the
    // "required" message; the domain message is standardized across the form.
    const SCE_EMAIL = /^[^\s@]+@sce\.com$/i;
    const reqEmail = (path, label) => {
      const val = String(get(path) ?? '').trim();
      if (!val) {
        e[path] = `${label} is required.`;
      } else if (!SCE_EMAIL.test(val)) {
        e[path] = 'Must be a valid @sce.com address.';
      }
    };

    // CMDB is required even for drafts (it's the partition key / identity).
    req('cmdbId', 'CMDB CI Name is required.');
    if (asDraft) return e;

    // Section 1
    req('application.requestType', 'Select a request type.');
    req('application.name', 'Product name is required.');
    req('application.appType', 'Select an application type.');
    reqMin('application.description', 10, 'Application description');

    // Section 2
    reqMin('businessJustification', 20, 'Business justification');

    // Section 3 & 4
    reqEmail('ownership.businessOwner.email', 'Business Owner email');
    req('ownership.businessOwner.name', 'Business Owner name is required.');
    req('support.groupName', 'Support group is required.');
    reqEmail('support.groupEmail', 'Support group email');

    // Section 6 — Authentication is mandatory for EVERY request type.
    req('authQuestionnaire.consumerType', 'Select a consumer type.');
    req('authQuestionnaire.interactionPattern', 'Select an interaction pattern.');

    // Conditional sections. Capability (5), Financial (7) and Cost Allocation
    // (8) are mandatory for a NEW onboarding and for EXISTING-application
    // changes, but OPTIONAL for an "Authentication Change" (which only alters
    // how an already-onboarded app authenticates). Keep this in sync with the
    // backend validateForSubmit() in routes/onboarding.js.
    const authChangeOnly = form.application.requestType === 'Authentication Change';
    if (!authChangeOnly) {
      // Section 5
      req('capability.types', 'Select at least one capability type.');
      req('capability.usagePatterns', 'Select at least one usage pattern.');
      reqMin('capability.usageDescription', 10, 'API usage description');

      // Section 7 & 8
      const budget = Number(form.financial.monthlyBudgetUsd);
      if (!String(form.financial.monthlyBudgetUsd ?? '').trim() || Number.isNaN(budget) || budget <= 0) {
        e['financial.monthlyBudgetUsd'] = 'Enter a monthly budget greater than 0.';
      }
      req('financial.overrunAction', 'Select a budget overrun action.');
      req('costAllocation.workOrderNumber', 'Work order number is required.');
      req('costAllocation.workOrderType', 'Select a work order type.');
    }

    return e;
  };

  // Live validity for the whole form (used by the progress tracker + per-field
  // green checks). Independent of which fields are "touched".
  const liveErrors = computeErrors(false);

  // A field's error shows only after it's been touched or a submit was tried.
  const errFor = (path) => {
    if (fieldErrors[path]) return fieldErrors[path];
    if ((touched[path] || submitAttempted) && liveErrors[path]) return liveErrors[path];
    return null;
  };
  // A field is "valid" (green) once it's been touched and has a real value.
  const isValid = (path) => {
    const val = get(path);
    const hasVal = Array.isArray(val) ? val.length > 0 : !!String(val ?? '').trim();
    return (touched[path] || submitAttempted) && !liveErrors[path] && hasVal;
  };

  // Per-field class + attribute helpers.
  const cls = (path) => (errFor(path) ? inputError : isValid(path) ? inputValid : input);
  const mark = (path) => (errFor(path) ? { 'data-field-error': 'true' } : {});
  const blur = (path) => () => markTouched(path);

  // Per-section completion for the progress tracker.
  const sectionDone = (section) => section.fields.every((f) => !liveErrors[f]);
  const completedCount = SECTIONS.filter(sectionDone).length;
  const progressPct = Math.round((completedCount / SECTIONS.length) * 100);

  // When the request is an "Authentication Change", Sections 5/7/8 are optional
  // (see computeErrors). Used to toggle the `required` asterisks in the UI so
  // the visual matches the actual validation rules.
  const authChangeOnly = form.application.requestType === 'Authentication Change';

  // Governance check on CMDB blur.
  const checkCmdb = async () => {
    markTouched('cmdbId');
    if (!form.cmdbId) return;
    try {
      const [reqs, products] = await Promise.all([
        api.getOnboardingByCmdb(form.cmdbId),
        api.getApimProductsByCmdb(form.cmdbId).catch(() => null),
      ]);
      setExisting({ requests: reqs.requests || [], products: products || null });
    } catch {
      setExisting(null);
    }
  };

  // Business Owner email — validate @sce.com and auto-populate the name from
  // Microsoft Graph when a valid address is entered. If Graph can't resolve the
  // user (e.g. local dev / SKIP_AUTH, or Graph permissions not yet granted),
  // fall back to manual name entry instead of blocking the form.
  const checkBusinessOwner = async () => {
    markTouched('ownership.businessOwner.email');
    setOwnerHint(null);
    setOwnerResolved(false);
    const email = (form.ownership.businessOwner.email || '').trim();
    if (!email || !/^[^\s@]+@sce\.com$/i.test(email)) return;
    setOwnerLookupBusy(true);
    try {
      const { user } = await api.lookupOnboardingUser(email);
      if (user?.name) {
        set('ownership.businessOwner.name', user.name);
        markTouched('ownership.businessOwner.name');
        setOwnerHint(null);
        setOwnerResolved(true);
      } else {
        setOwnerHint("Couldn't auto-resolve this user — please enter the name manually.");
      }
    } catch {
      setOwnerHint("Couldn't auto-resolve this user — please enter the name manually.");
    } finally {
      setOwnerLookupBusy(false);
    }
  };

  // Returns a map of { 'dot.path': 'message' } for every invalid required
  // field. Drafts are lenient — only the CMDB CI Name is mandatory.
  const validate = (asDraft) => {
    const e = {};
    const req = (path, val, msg) => {
      const empty = Array.isArray(val) ? val.length === 0 : !String(val ?? '').trim();
      if (empty) e[path] = msg || 'This field is required.';
    };

    // CMDB is required even for drafts (it's the partition key / identity).
    req('cmdbId', form.cmdbId, 'CMDB CI Name is required.');
    if (asDraft) return e;

    // Section 1
    req('application.requestType', form.application.requestType, 'Select a request type.');
    req('application.appType', form.application.appType, 'Select an application type.');
    req('application.description', form.application.description, 'Add an application description.');

    // Section 2
    req('businessJustification', form.businessJustification, 'Describe the business purpose / justification.');

    // Section 3 & 4
    const ownerEmail = (form.ownership.businessOwner.email || '').trim();
    if (!ownerEmail) {
      e['ownership.businessOwner.email'] = 'Business Owner email is required.';
    } else if (!/^[^\s@]+@sce\.com$/i.test(ownerEmail)) {
      e['ownership.businessOwner.email'] = 'Must be a valid @sce.com address.';
    }
    req('ownership.businessOwner.name', form.ownership.businessOwner.name, 'Business Owner name is required.');
    req('support.groupName', form.support.groupName, 'Support group is required.');
    req('support.groupEmail', form.support.groupEmail, 'Support group email is required.');

    // Section 5
    req('capability.types', form.capability.types, 'Select at least one capability type.');
    req('capability.usagePatterns', form.capability.usagePatterns, 'Select at least one usage pattern.');
    req('capability.usageDescription', form.capability.usageDescription, 'Describe the API usage.');

    // Section 6
    req('authQuestionnaire.consumerType', form.authQuestionnaire.consumerType, 'Select a consumer type.');
    req('authQuestionnaire.interactionPattern', form.authQuestionnaire.interactionPattern, 'Select an interaction pattern.');

    // Section 7 & 8
    const budget = Number(form.financial.monthlyBudgetUsd);
    if (!String(form.financial.monthlyBudgetUsd ?? '').trim() || Number.isNaN(budget) || budget <= 0) {
      e['financial.monthlyBudgetUsd'] = 'Enter a monthly budget greater than 0.';
    }
    req('financial.overrunAction', form.financial.overrunAction, 'Select a budget overrun action.');
    req('costAllocation.workOrderNumber', form.costAllocation.workOrderNumber, 'Work order number is required.');
    req('costAllocation.workOrderType', form.costAllocation.workOrderType, 'Select a work order type.');

    return e;
  };

  const submit = async (asDraft) => {
    setBusy(true);
    setError(null);
    setSubmitAttempted(true);
    // Validate before sending. Drafts only need a CMDB CI Name; a full submit
    // enforces every required field and surfaces errors inline next to each.
    const errors = computeErrors(asDraft);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      // Mark every invalid field as touched so its message is visible.
      setTouched((prev) => {
        const next = { ...prev };
        Object.keys(errors).forEach((k) => { next[k] = true; });
        return next;
      });
      setError(
        asDraft
          ? 'Please provide a CMDB CI Name to save a draft.'
          : `Please fix ${Object.keys(errors).length} field(s) highlighted below before submitting.`,
      );
      setBusy(false);
      // Scroll the first error into view for convenience.
      requestAnimationFrame(() => {
        document.querySelector('[data-field-error="true"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    setFieldErrors({});
    try {
      const payload = { ...form, financial: { ...form.financial, monthlyBudgetUsd: Number(form.financial.monthlyBudgetUsd) || 0 } };
      if (editing) {
        // Editing an existing Draft: PUT to save edits, or promote to Submitted.
        const id = editing.id;
        if (asDraft) {
          await api.updateOnboarding(id, payload);
        } else {
          await api.submitOnboardingDraft(id, payload);
        }
      } else {
        await api.submitOnboarding(payload, { draft: asDraft });
      }
      setDirty(false);
      onSubmitted(asDraft);
    } catch (e) {
      setError(e.message || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  if (!enums) return <div className="text-gray-500">Loading form…</div>;

  const jumpTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-6 items-start">
      {/* Sticky progress tracker / section nav */}
      <aside className="hidden lg:block sticky top-4 self-start">
        <div className={card + ' p-4'}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</span>
            <span className="text-xs font-semibold text-sce-green">{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-sce-green transition-all duration-300" style={{ width: `${progressPct}%` }} />
          </div>
          <nav className="space-y-0.5">
            {SECTIONS.map((s, i) => {
              const done = sectionDone(s);
              return (
                <button key={s.id} type="button" onClick={() => jumpTo(s.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs hover:bg-gray-50 transition-colors">
                  <span className={'shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ' +
                    (done ? 'bg-sce-green text-white' : 'bg-gray-100 text-gray-500')}>
                    {done ? (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : i + 1}
                  </span>
                  <span className={done ? 'text-gray-700 font-medium' : 'text-gray-500'}>{s.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      <div className="space-y-5 max-w-4xl">
        {/* Mobile progress bar */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progress</span>
            <span className="text-xs font-semibold text-sce-green">{completedCount} / {SECTIONS.length} sections</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-sce-green transition-all duration-300" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2" role="alert">
          <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="font-medium">{error}</p>
            {Object.keys(fieldErrors).length > 0 && (
              <p className="text-xs text-red-600 mt-0.5">Fields needing attention are highlighted in red below.</p>
            )}
          </div>
        </div>
      )}

      {/* Section 1 */}
      <div className={card} id="sec-1">
        <h3 className={sectionTitle}>Application Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field labelText="CMDB CI Name" required error={errFor('cmdbId')} valid={isValid('cmdbId')}
            hint={editing ? 'CMDB CI Name is fixed once a request exists.' : undefined}>
            <input className={cls('cmdbId')} {...mark('cmdbId')} value={form.cmdbId} onBlur={checkCmdb}
              readOnly={!!editing}
              onChange={(e) => { if (editing) return; set('cmdbId', e.target.value); }}
              placeholder="e.g. Enterprise AI Services Platform" />
          </Field>
          <Field labelText="Product Name" required error={errFor('application.name')} valid={isValid('application.name')}>
            <input className={cls('application.name')} {...mark('application.name')} value={form.application.name}
              onBlur={blur('application.name')}
              onChange={(e) => set('application.name', e.target.value)}
              placeholder="e.g. Enterprise AI Services Platform" />
          </Field>
          <Field labelText="Request Type" required error={errFor('application.requestType')} valid={isValid('application.requestType')}>
            <select className={cls('application.requestType')} {...mark('application.requestType')} value={form.application.requestType}
              onBlur={blur('application.requestType')}
              onChange={(e) => set('application.requestType', e.target.value)}>
              <option value="">Select…</option>
              {enums.requestType.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field labelText="Application Type" required error={errFor('application.appType')} valid={isValid('application.appType')}>
            <select className={cls('application.appType')} {...mark('application.appType')} value={form.application.appType}
              onBlur={blur('application.appType')}
              onChange={(e) => set('application.appType', e.target.value)}>
              <option value="">Select…</option>
              {enums.appType.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field labelText="Application Description" required error={errFor('application.description')} valid={isValid('application.description')}>
              <textarea className={cls('application.description')} {...mark('application.description')} rows={2} value={form.application.description}
                onBlur={blur('application.description')}
                onChange={(e) => set('application.description', e.target.value)} />
            </Field>
          </div>
        </div>
        {existing && (
          <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="font-medium text-amber-800 flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Governance check
            </p>
            <p className="mt-1 text-amber-700">
              {existing.requests.length} existing onboarding request(s) for this CMDB.
            </p>
            {existing.products?.correlated?.length ? (
              <div className="mt-1 text-amber-700">
                {existing.products.correlated.length} possibly-related APIM product(s) (name match):
                <ul className="mt-0.5 list-disc list-inside text-amber-600">
                  {existing.products.correlated.slice(0, 5).map((p) => (
                    <li key={`${p.service}:${p.id}`}>{p.displayName || p.name} <span className="text-amber-400">· {p.service}</span></li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="mt-1 text-amber-600">No related APIM products matched by name.</p>
            )}
          </div>
        )}
      </div>

      {/* Section 2 */}
      <div className={card} id="sec-2">
        <h3 className={sectionTitle}>Business Purpose</h3>
        <Field labelText="Business Purpose and APIM Access Justification" required error={errFor('businessJustification')} valid={isValid('businessJustification')}>
          <textarea className={cls('businessJustification')} {...mark('businessJustification')} rows={3} value={form.businessJustification}
            onBlur={blur('businessJustification')}
            onChange={(e) => set('businessJustification', e.target.value)} />
        </Field>
      </div>

      {/* Section 3 + 4 */}
      <div className={card} id="sec-3">
        <h3 className={sectionTitle}>Ownership & Support</h3>

        {/* 3.1 Requestor + 3.2 Manager — auto-populated, read-only. Saved with
            the request; the manager receives notifications / future approvals. */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            <div>
              <span className="text-gray-500">Requestor</span>
              <p className="font-medium text-sce-dark">{prefill?.requestor?.name || '—'}</p>
              <p className="text-xs text-gray-500">{prefill?.requestor?.email || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">Manager</span>
              <p className="font-medium text-sce-dark">{prefill?.manager?.name || 'Not resolved'}</p>
              <p className="text-xs text-gray-500">{prefill?.manager?.email || '—'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field labelText="Business Owner Email" required error={errFor('ownership.businessOwner.email')} valid={isValid('ownership.businessOwner.email')}>
            <input className={cls('ownership.businessOwner.email')} {...mark('ownership.businessOwner.email')} type="email" value={form.ownership.businessOwner.email}
              onBlur={checkBusinessOwner}
              onChange={(e) => set('ownership.businessOwner.email', e.target.value)}
              placeholder="name@sce.com" />
            {ownerHint && <p className="text-xs text-amber-600 mt-1">{ownerHint}</p>}
          </Field>
          <Field labelText="Business Owner Name" required error={errFor('ownership.businessOwner.name')} valid={isValid('ownership.businessOwner.name')}>
            <input className={cls('ownership.businessOwner.name')} {...mark('ownership.businessOwner.name')} value={form.ownership.businessOwner.name}
              onBlur={blur('ownership.businessOwner.name')}
              onChange={(e) => { setOwnerResolved(false); set('ownership.businessOwner.name', e.target.value); }}
              placeholder={ownerLookupBusy ? 'Looking up…' : 'Enter the business owner’s full name'} />
            {ownerResolved && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Resolved from directory — edit if needed.
              </p>
            )}
          </Field>
          <Field labelText="Support Group" required error={errFor('support.groupName')} valid={isValid('support.groupName')}>
            <input className={cls('support.groupName')} {...mark('support.groupName')} value={form.support.groupName}
              onBlur={blur('support.groupName')}
              onChange={(e) => set('support.groupName', e.target.value)} />
          </Field>
          <Field labelText="Support Group Email" required error={errFor('support.groupEmail')} valid={isValid('support.groupEmail')}>
            <input className={cls('support.groupEmail')} {...mark('support.groupEmail')} value={form.support.groupEmail}
              onBlur={blur('support.groupEmail')}
              onChange={(e) => set('support.groupEmail', e.target.value)} />
          </Field>
          <Field labelText="AD Group" error={errFor('support.adGroup')} valid={isValid('support.adGroup')}
            hint="Optional — Active Directory security group, if applicable.">
            <input className={cls('support.adGroup')} {...mark('support.adGroup')} value={form.support.adGroup}
              onBlur={blur('support.adGroup')}
              onChange={(e) => set('support.adGroup', e.target.value)}
              placeholder="e.g. APP-MyTeam-Users" />
          </Field>
        </div>
      </div>

      {/* Section 5 */}
      <div className={card} id="sec-5">
        <h3 className={sectionTitle}>Capability & Usage</h3>
        {authChangeOnly && (
          <p className="text-xs text-gray-500 -mt-2 mb-3">Optional for an Authentication Change request.</p>
        )}
        <div className="space-y-4">
          <Field labelText="Requested Capability Type" required={!authChangeOnly} error={errFor('capability.types')} valid={isValid('capability.types')}>
            <MultiSelect options={enums.capabilityType} value={form.capability.types}
              onChange={(v) => { set('capability.types', v); markTouched('capability.types'); }} />
          </Field>
          <Field labelText="Intended Usage Pattern" required={!authChangeOnly} error={errFor('capability.usagePatterns')} valid={isValid('capability.usagePatterns')}>
            <MultiSelect options={enums.usagePattern} value={form.capability.usagePatterns}
              onChange={(v) => { set('capability.usagePatterns', v); markTouched('capability.usagePatterns'); }} />
          </Field>
          <Field labelText="Description of API Usage" required={!authChangeOnly} error={errFor('capability.usageDescription')} valid={isValid('capability.usageDescription')}>
            <textarea className={cls('capability.usageDescription')} {...mark('capability.usageDescription')} rows={3} value={form.capability.usageDescription}
              onBlur={blur('capability.usageDescription')}
              onChange={(e) => set('capability.usageDescription', e.target.value)} />
          </Field>
        </div>
      </div>

      {/* Section 6 */}
      <div className={card} id="sec-6">
        <h3 className={sectionTitle}>Authentication</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field labelText="Consumer Type" required error={errFor('authQuestionnaire.consumerType')} valid={isValid('authQuestionnaire.consumerType')}>
            <select className={cls('authQuestionnaire.consumerType')} {...mark('authQuestionnaire.consumerType')} value={form.authQuestionnaire.consumerType}
              onBlur={blur('authQuestionnaire.consumerType')}
              onChange={(e) => set('authQuestionnaire.consumerType', e.target.value)}>
              <option value="">Select…</option>
              {enums.consumerType.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field labelText="Application Interaction Pattern" required error={errFor('authQuestionnaire.interactionPattern')} valid={isValid('authQuestionnaire.interactionPattern')}>
            <select className={cls('authQuestionnaire.interactionPattern')} {...mark('authQuestionnaire.interactionPattern')} value={form.authQuestionnaire.interactionPattern}
              onBlur={blur('authQuestionnaire.interactionPattern')}
              onChange={(e) => set('authQuestionnaire.interactionPattern', e.target.value)}>
              <option value="">Select…</option>
              {enums.interactionPattern.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field labelText="Can obtain/pass Entra ID JWT?">
            <select className={input} value={form.authQuestionnaire.entraCapable}
              onChange={(e) => set('authQuestionnaire.entraCapable', e.target.value)}>
              <option value="">Select…</option>
              {enums.yesNoNotSure.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field labelText="Can attach JWT on outbound calls?">
            <select className={input} value={form.authQuestionnaire.jwtOutbound}
              onChange={(e) => set('authQuestionnaire.jwtOutbound', e.target.value)}>
              <option value="">Select…</option>
              {enums.yesNoNotSure.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>

        {/* The system-recommended authentication pattern is computed server-side
            and conveyed to the EASP approvers / support team via email — it is
            intentionally not shown to the requestor here. */}
      </div>

      {/* Section 7 + 8 */}
      <div className={card} id="sec-7">
        <h3 className={sectionTitle}>Financial & Cost</h3>
        {authChangeOnly && (
          <p className="text-xs text-gray-500 -mt-2 mb-3">Optional for an Authentication Change request.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field labelText="Monthly Budget (USD)" required={!authChangeOnly} error={errFor('financial.monthlyBudgetUsd')} valid={isValid('financial.monthlyBudgetUsd')}>
            <input type="number" className={cls('financial.monthlyBudgetUsd')} {...mark('financial.monthlyBudgetUsd')} value={form.financial.monthlyBudgetUsd}
              onBlur={blur('financial.monthlyBudgetUsd')}
              onChange={(e) => set('financial.monthlyBudgetUsd', e.target.value)} placeholder="e.g. 500" />
          </Field>
          <Field labelText="Budget Overrun Action" required={!authChangeOnly} error={errFor('financial.overrunAction')} valid={isValid('financial.overrunAction')}>
            <select className={cls('financial.overrunAction')} {...mark('financial.overrunAction')} value={form.financial.overrunAction}
              onBlur={blur('financial.overrunAction')}
              onChange={(e) => set('financial.overrunAction', e.target.value)}>
              <option value="">Select…</option>
              {enums.overrunAction.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field labelText="Work Order Number" required={!authChangeOnly} error={errFor('costAllocation.workOrderNumber')} valid={isValid('costAllocation.workOrderNumber')}>
            <input className={cls('costAllocation.workOrderNumber')} {...mark('costAllocation.workOrderNumber')} value={form.costAllocation.workOrderNumber}
              onBlur={blur('costAllocation.workOrderNumber')}
              onChange={(e) => set('costAllocation.workOrderNumber', e.target.value)} />
          </Field>
          <Field labelText="Work Order Type" required={!authChangeOnly} error={errFor('costAllocation.workOrderType')} valid={isValid('costAllocation.workOrderType')}>
            <select className={cls('costAllocation.workOrderType')} {...mark('costAllocation.workOrderType')} value={form.costAllocation.workOrderType}
              onBlur={blur('costAllocation.workOrderType')}
              onChange={(e) => set('costAllocation.workOrderType', e.target.value)}>
              <option value="">Select…</option>
              {enums.workOrderType.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 -mx-1 bg-white/90 backdrop-blur border-t border-gray-200 rounded-b-xl px-1 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-xs text-gray-500 sm:mr-auto">
          {editing && <span className="text-sce-green font-medium">Editing draft · </span>}
          {completedCount === SECTIONS.length
            ? 'All sections complete — ready to submit.'
            : `${completedCount} of ${SECTIONS.length} sections complete.`}
        </span>
        {editing && (
          <button disabled={busy} onClick={onCancelEdit}
            className="px-5 py-2.5 bg-white border border-gray-300 text-gray-600 rounded-lg font-medium hover:border-gray-400 disabled:opacity-50 w-full sm:w-auto order-3">
            Cancel
          </button>
        )}
        <button disabled={busy} onClick={() => submit(true)}
          className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:border-sce-green disabled:opacity-50 w-full sm:w-auto order-2 sm:order-1">
          {editing ? 'Save Draft' : 'Save as Draft'}
        </button>
        <button disabled={busy} onClick={() => submit(false)}
          className="px-5 py-2.5 bg-sce-green text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 w-full sm:w-auto order-1 sm:order-2">
          {busy ? 'Submitting…' : 'Submit Request'}
        </button>
      </div>
      </div>
    </div>
  );
}

// ─── Request list (shared) ──────────────────────────────────────────────
function RequestRow({ r, onOpen }) {
  const initials = (r.application?.name || '?').trim().slice(0, 2).toUpperCase();
  return (
    <button onClick={() => onOpen(r)}
      className="group w-full text-left px-4 py-3.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 transition-colors">
      <div className="shrink-0 w-9 h-9 rounded-lg bg-sce-green/10 text-sce-green flex items-center justify-center text-xs font-semibold">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-sce-dark truncate">
          {r.application?.name || '(unnamed)'}
          <span className="text-gray-400 font-normal"> · {r.cmdbId}</span>
        </p>
        <p className="text-xs text-gray-500 truncate">
          {(r.capability?.types || []).join(', ') || '—'}
          {r.authRecommendation?.pattern ? ` · ${r.authRecommendation.pattern}` : ''}
        </p>
      </div>
      <StatusBadge status={r.status} />
      <svg className="shrink-0 w-4 h-4 text-gray-300 group-hover:text-gray-400" fill="none" viewBox="0 0 24 24"
        stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

function RequestDetail({ r, isApprover, fromQueue, onClose, onDecided, onEdit }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Decision controls are available to EASP approvers when the request is
  // opened from the Approval Queue. The "My Requests" view is always read-only
  // so a requestor can track — but not decide — their own submissions.
  const canDecide = isApprover && fromQueue;
  // Draft owners (opened from My Requests) can edit their own draft.
  const canEdit = !fromQueue && r.status === 'Draft' && typeof onEdit === 'function';

  const decide = async (decision) => {
    if (decision === 'reject' && !note) {
      setError('A note is required when rejecting.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.decideOnboarding(r.id, { decision, note, cmdbId: r.cmdbId });
      onDecided(decision);
    } catch (e) {
      setError(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] flex flex-col overflow-hidden"
        role="dialog" aria-modal="true" aria-label="Request details"
        onClick={(e) => e.stopPropagation()}>
        {/* Sticky header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-sce-dark truncate">{r.application?.name || '(unnamed)'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">CMDB · {r.cmdbId}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={r.status} />
            <button onClick={onClose} aria-label="Close"
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-4 overflow-y-auto">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Request Type</dt>
              <dd className="text-sce-dark">{r.application?.requestType || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Requestor</dt>
              <dd className="text-sce-dark">{r.ownership?.requestor?.email || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Product Name</dt>
              <dd className="text-sce-dark">{r.application?.name || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Application Type</dt>
              <dd className="text-sce-dark">{r.application?.appType || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Business Owner</dt>
              <dd className="text-sce-dark">
                {r.ownership?.businessOwner?.name || '—'}
                {r.ownership?.businessOwner?.email && (
                  <span className="text-gray-400"> · {r.ownership.businessOwner.email}</span>
                )}
              </dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Support Group</dt>
              <dd className="text-sce-dark">
                {r.support?.groupName || '—'}
                {r.support?.groupEmail && <span className="text-gray-400"> · {r.support.groupEmail}</span>}
                {r.support?.adGroup && <span className="text-gray-400"> · AD: {r.support.adGroup}</span>}
              </dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Capability</dt>
              <dd className="text-sce-dark">{(r.capability?.types || []).join(', ') || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Usage Patterns</dt>
              <dd className="text-sce-dark">{(r.capability?.usagePatterns || []).join(', ') || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Auth Pattern</dt>
              <dd className="text-sce-dark">{r.authRecommendation?.pattern || '—'} {r.authRecommendation?.requiresException && <span className="text-purple-600">(exception)</span>}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Monthly Budget</dt>
              <dd className="text-sce-dark">${r.financial?.monthlyBudgetUsd ?? '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Budget Overrun Action</dt>
              <dd className="text-sce-dark">{r.financial?.overrunAction || '—'}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-xs uppercase tracking-wide text-gray-400">Work Order</dt>
              <dd className="text-sce-dark">{r.costAllocation?.workOrderNumber || '—'} {r.costAllocation?.workOrderType && `(${r.costAllocation.workOrderType})`}</dd>
            </div>
          </dl>

          <div className="mt-4 space-y-2 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Business Justification</p>
              <p className="text-sce-dark">{r.businessJustification || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">API Usage</p>
              <p className="text-sce-dark">{r.capability?.usageDescription || '—'}</p>
            </div>
          </div>

          {/* Authentication details — the Section 6 answers plus the derived
              recommendation + rationale, so the approver can see WHY a pattern
              was recommended and whether an exception is justified. */}
          {r.authQuestionnaire && (
            <div className="mt-4 border border-gray-200 rounded-lg p-3">
              <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Authentication Details</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex flex-col">
                  <dt className="text-xs text-gray-400">Consumer Type</dt>
                  <dd className="text-sce-dark">{r.authQuestionnaire.consumerType || '—'}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-xs text-gray-400">Interaction Pattern</dt>
                  <dd className="text-sce-dark">{r.authQuestionnaire.interactionPattern || '—'}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-xs text-gray-400">Can obtain/pass Entra ID JWT?</dt>
                  <dd className="text-sce-dark">{r.authQuestionnaire.entraCapable || '—'}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-xs text-gray-400">Can attach JWT on outbound?</dt>
                  <dd className="text-sce-dark">{r.authQuestionnaire.jwtOutbound || '—'}</dd>
                </div>
                {r.authQuestionnaire.altIdp?.uses && (
                  <div className="flex flex-col">
                    <dt className="text-xs text-gray-400">Alternative IdP</dt>
                    <dd className="text-sce-dark">{r.authQuestionnaire.altIdp.name || 'Yes'}</dd>
                  </div>
                )}
                {r.authQuestionnaire.technicalConstraints?.hasConstraints && (
                  <div className="flex flex-col sm:col-span-2">
                    <dt className="text-xs text-gray-400">Technical Constraints</dt>
                    <dd className="text-sce-dark">{r.authQuestionnaire.technicalConstraints.details || 'Declared'}</dd>
                  </div>
                )}
              </dl>

              {/* Derived recommendation + rationale. */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-1">
                  Recommended Pattern
                  {r.authRecommendation?.requiresException && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-semibold uppercase">
                      Exception Required
                    </span>
                  )}
                </p>
                <p className="text-sce-dark font-medium">{r.authRecommendation?.pattern || '—'}</p>
                {r.authRecommendation?.rationale && (
                  <p className="text-xs text-gray-500 mt-1">{r.authRecommendation.rationale}</p>
                )}
                {r.authQuestionnaire.exceptionJustification && (
                  <div className="mt-2 bg-purple-50 border border-purple-100 rounded-lg p-2">
                    <p className="text-xs uppercase tracking-wide text-purple-600 mb-0.5">Exception Justification</p>
                    <p className="text-xs text-purple-900">{r.authQuestionnaire.exceptionJustification}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Suggested endpoints (automation output). Shown to approvers so they
              can review + select. Budget-over candidates are shown-but-flagged. */}
          {isApprover && r.endpointRecommendations && (
            (r.endpointRecommendations.candidates?.length > 0
              || r.endpointRecommendations.notes?.length > 0) && (
              <div className="mt-4 border border-sce-green/30 bg-sce-green/5 rounded-lg p-3">
                <p className="text-xs uppercase tracking-wide text-sce-green font-semibold mb-2 flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Suggested Endpoints <span className="text-gray-400 font-normal normal-case">· automation · review &amp; select</span>
                </p>
                {r.endpointRecommendations.candidates?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-400">
                          <th className="py-1 pr-3 font-medium">Endpoint</th>
                          <th className="py-1 pr-3 font-medium">Type</th>
                          <th className="py-1 pr-3 font-medium">Match</th>
                          <th className="py-1 pr-3 font-medium">Budget</th>
                          <th className="py-1 font-medium">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.endpointRecommendations.candidates.map((c, i) => (
                          <tr key={`${c.type}:${c.name}:${i}`} className="border-t border-gray-100 align-top">
                            <td className="py-1.5 pr-3 font-medium text-sce-dark">{c.name}</td>
                            <td className="py-1.5 pr-3 text-gray-600">{c.type}</td>
                            <td className="py-1.5 pr-3 text-gray-600">{typeof c.score === 'number' ? `${Math.round(c.score * 100)}%` : '—'}</td>
                            <td className="py-1.5 pr-3">
                              {c.overBudget ? (
                                <span className="text-amber-600 font-medium">⚠ over budget</span>
                              ) : c.budgetFit === 'tight' ? (
                                <span className="text-amber-600">near budget</span>
                              ) : c.budgetFit === 'fit' ? (
                                <span className="text-green-600">fits</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="py-1.5 text-gray-500">{c.rationale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {r.endpointRecommendations.notes?.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-xs text-amber-700 space-y-0.5">
                    {r.endpointRecommendations.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
              </div>
            )
          )}

          {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          {canDecide && ['Submitted', 'InReview'].includes(r.status) && (
            <div className="mt-5 border-t border-gray-100 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Decision note <span className="text-gray-400 font-normal">(required to reject)</span></label>
              <textarea className={input} rows={2} placeholder="Add a reason or note…"
                value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="flex flex-col sm:flex-row gap-2.5 mt-3">
                <button disabled={busy} onClick={() => decide('approve')}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  Approve
                </button>
                <button disabled={busy} onClick={() => decide('reject')}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  Reject
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer — only shown when there's an action; the header X closes. */}
        {canEdit && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2">
            <button onClick={() => onEdit(r)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-sce-green text-white rounded-lg hover:opacity-90">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit Draft
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────
export default function Onboarding() {
  const { hasRole } = useAuth();
  // easpApproverRole is an array of role names — the queue is visible if the
  // user holds ANY of them (configurable via REACT_APP_EASP_APPROVER_ROLE).
  const isApprover = [].concat(appConfig.rbac.easpApproverRole || []).some((r) => hasRole(r));
  const [tab, setTab] = useState('new');
  const [enums, setEnums] = useState(null);
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [all, setAll] = useState([]);
  const [allStatus, setAllStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  // When set, the New Request tab renders in "edit draft" mode seeded with this
  // request. Cleared on submit/save/cancel.
  const [editingDraft, setEditingDraft] = useState(null);

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (message, kind = 'success') => setToast({ message, kind });

  useEffect(() => {
    api.getOnboardingEnums().then((r) => setEnums(r.enums)).catch(() => {});
  }, []);

  const refreshMine = useCallback(() => {
    api.getMyOnboarding().then((r) => setMine(r.requests || [])).catch(() => {});
  }, []);
  const refreshQueue = useCallback(() => {
    if (isApprover) api.getOnboardingQueue().then((r) => setQueue(r.requests || [])).catch(() => {});
  }, [isApprover]);
  const refreshAll = useCallback(() => {
    if (isApprover) api.getAllOnboarding(allStatus || undefined).then((r) => setAll(r.requests || [])).catch(() => {});
  }, [isApprover, allStatus]);

  useEffect(() => { refreshMine(); refreshQueue(); }, [refreshMine, refreshQueue]);
  useEffect(() => { refreshAll(); }, [refreshAll]);

  const tabs = [
    { id: 'new', label: editingDraft ? 'Edit Draft' : 'New Request' },
    { id: 'mine', label: `My Requests (${mine.length})` },
    ...(isApprover ? [{ id: 'queue', label: `Approval Queue (${queue.length})` }] : []),
    ...(isApprover ? [{ id: 'all', label: `All Requests (${all.length})` }] : []),
  ];

  // Open a Draft in the New Request form for editing.
  const startEditDraft = (r) => {
    setSelected(null);
    setEditingDraft(r);
    setTab('new');
  };

  const EmptyState = ({ icon, title, subtitle }) => (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
        {icon}
      </div>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-sce-dark">APIM Product Onboarding</h1>
        <p className="text-gray-500 text-sm">Submit and track onboarding requests for APIM-exposed capabilities.</p>
      </div>

      <div className="flex gap-1 mb-5 border-b border-gray-200 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ' +
              (tab === t.id ? 'border-sce-green text-sce-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'new' && (
        <NewRequestForm
          key={editingDraft ? editingDraft.id : 'new'}
          enums={enums}
          editing={editingDraft}
          onCancelEdit={() => { setEditingDraft(null); setTab('mine'); }}
          onSubmitted={(asDraft) => {
            const wasEditing = !!editingDraft;
            setEditingDraft(null);
            refreshMine();
            setTab('mine');
            showToast(
              asDraft
                ? (wasEditing ? 'Draft updated.' : 'Draft saved. You can finish it anytime from My Requests.')
                : 'Request submitted — the EASP team will review it shortly.',
            );
          }}
        />
      )}

      {tab === 'mine' && (
        <div className={card + ' max-w-3xl p-0 overflow-hidden'}>
          {mine.length === 0 ? (
            <EmptyState
              icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              title="No requests yet"
              subtitle="Submit a new request to get started."
            />
          ) : mine.map((r) => <RequestRow key={r.id} r={r} onOpen={(req) => setSelected({ req, fromQueue: false })} />)}
        </div>
      )}

      {tab === 'queue' && isApprover && (
        <div className={card + ' max-w-3xl p-0 overflow-hidden'}>
          {queue.length === 0 ? (
            <EmptyState
              icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              title="Nothing awaiting approval"
              subtitle="New submissions will appear here."
            />
          ) : queue.map((r) => <RequestRow key={r.id} r={r} onOpen={(req) => setSelected({ req, fromQueue: true })} />)}
        </div>
      )}

      {tab === 'all' && isApprover && (
        <div className="max-w-3xl space-y-3">
          {/* Status filter — defaults to all statuses. */}
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { v: '', label: 'All' },
              { v: 'Draft', label: 'Draft' },
              { v: 'Submitted', label: 'Submitted' },
              { v: 'Approved', label: 'Approved' },
              { v: 'Rejected', label: 'Rejected' },
            ].map((f) => (
              <button key={f.v || 'all'} type="button" onClick={() => setAllStatus(f.v)}
                className={'px-3 py-1.5 rounded-full text-xs font-medium border transition ' +
                  (allStatus === f.v
                    ? 'bg-sce-green text-white border-sce-green'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-sce-green')}>
                {f.label}
              </button>
            ))}
          </div>
          <div className={card + ' p-0 overflow-hidden'}>
            {all.length === 0 ? (
              <EmptyState
                icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>}
                title="No requests found"
                subtitle={allStatus ? `No requests with status "${allStatus}".` : 'Submitted requests will appear here.'}
              />
            ) : all.map((r) => (
              <RequestRow key={r.id} r={r} onOpen={(req) => setSelected({ req, fromQueue: false })} />
            ))}
          </div>
        </div>
      )}

      {selected && (
        <RequestDetail r={selected.req} isApprover={isApprover} fromQueue={selected.fromQueue}
          onClose={() => setSelected(null)}
          onEdit={startEditDraft}
          onDecided={(decision) => {
            setSelected(null);
            refreshQueue();
            refreshMine();
            refreshAll();
            if (decision) showToast(`Request ${decision === 'approve' ? 'approved' : 'rejected'}.`, decision === 'approve' ? 'success' : 'info');
          }} />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[60] animate-[fadeIn_0.2s_ease-out]">
          <div className={'flex items-start gap-2.5 rounded-xl shadow-lg px-4 py-3 text-sm max-w-sm border ' +
            (toast.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800'
              : toast.kind === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-gray-50 border-gray-200 text-gray-800')}>
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="flex-1">{toast.message}</p>
            <button onClick={() => setToast(null)} aria-label="Dismiss" className="shrink-0 text-current/60 hover:text-current">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
